import { useState } from 'react';
import {
  HomeIcon,
  InformationCircleIcon,
  ClockIcon,
  PhotoIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  ArrowLeftIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import MobileContentSectionManager from '../../components/MobileContentSectionManager';
import MobileRegistrationContentManager from '../../components/MobileRegistrationContentManager';
import MobileLogoManager from '../../components/MobileLogoManager';

interface ContentPage {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bg: string;
}

const contentPages: ContentPage[] = [
  { id: 'home', name: 'Home Page', description: 'Hero, about, tournament legacy sections', icon: HomeIcon, color: 'text-blue-600', bg: 'bg-blue-500' },
  { id: 'about', name: 'About Page', description: 'Mission, committee, testimonials', icon: InformationCircleIcon, color: 'text-teal-600', bg: 'bg-teal-500' },
  { id: 'hero-slider', name: 'Hero Slider', description: 'Homepage hero carousel slides', icon: RectangleStackIcon, color: 'text-golf-orange', bg: 'bg-golf-orange' },
  { id: 'registration', name: 'Registration Page', description: 'Registration content & key values', icon: DocumentTextIcon, color: 'text-rose-600', bg: 'bg-rose-500' },
  { id: 'history', name: 'History Page', description: 'Champions, galleries, press', icon: ClockIcon, color: 'text-amber-600', bg: 'bg-amber-500' },
  { id: 'gallery', name: 'Gallery Page', description: 'Photo galleries & categories', icon: PhotoIcon, color: 'text-emerald-600', bg: 'bg-emerald-500' },
  { id: 'logos', name: 'Logo Management', description: 'Website logos & branding', icon: SwatchIcon, color: 'text-slate-600', bg: 'bg-slate-500' },
];

export default function MobileAdminContent() {
  const [activePage, setActivePage] = useState<string | null>(null);

  const active = contentPages.find(p => p.id === activePage);

  if (activePage && active) {
    return (
      <div className="pb-4">
        <div className="sticky top-14 z-10 bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setActivePage(null)}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-gray-200 shadow-sm active:scale-95 transition-transform"
          >
            <ArrowLeftIcon className="w-4 h-4 text-golf-gray" />
          </button>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg ${active.bg} flex items-center justify-center`}>
              <active.icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-golf-gray leading-tight">{active.name}</p>
              <p className="text-xs text-gray-400">{active.description}</p>
            </div>
          </div>
        </div>
        {activePage === 'registration'
          ? <MobileRegistrationContentManager />
          : activePage === 'logos'
          ? <MobileLogoManager />
          : <MobileContentSectionManager page={activePage} />
        }
      </div>
    );
  }

  return (
    <div className="pb-4 space-y-4">
      <div className="px-4 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Content Management</p>
        <h1 className="text-xl font-display font-bold text-golf-gray">Manage Pages</h1>
        <p className="text-sm text-gray-500 mt-1">Edit content across all public pages</p>
      </div>

      <div className="px-4 space-y-2.5">
        {contentPages.map(page => (
          <button
            key={page.id}
            onClick={() => setActivePage(page.id)}
            className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 text-left active:bg-gray-50 active:scale-[0.99] transition-all"
          >
            <div className={`w-11 h-11 rounded-xl ${page.bg} flex items-center justify-center flex-shrink-0`}>
              <page.icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-golf-gray">{page.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{page.description}</p>
            </div>
            <ArrowLeftIcon className="w-4 h-4 text-gray-300 rotate-180 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
